import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, interval, timer, merge } from "rxjs";
import {map, takeUntil} from "rxjs/operators";
import { LocationService } from "../location.service";
import { LocalStorageService } from "../../local-storage.service";
import { StoreOrderService } from "../../stores/store-order/store-order.service";
import { StoreService } from "../store/store.service";
import { IntegrationPaymentService } from "./integration.payment.service";
import { Store } from "@ngrx/store";
import { IntegrationPaymentInProgress } from "./+state/payment.actions";

@Injectable()
export abstract class PaymentStatusFetchBase implements OnDestroy {
  protected unsubscribe$ = new Subject<void>();
  private stopped = false;
  private provider;

  protected constructor(
    protected locationService: LocationService,
    protected router: Router,
    protected storageService: LocalStorageService,
    protected storeOrderService: StoreOrderService,
    protected storeService: StoreService,
    protected store: Store,
    protected integrationPaymentService: IntegrationPaymentService
  ) {}

  onInit(): void {
    this.store.dispatch(new IntegrationPaymentInProgress());
    this.fetchPaymentStatus();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private cleanup(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private startPolling(): void {
    const { intervalMs, timeoutMs } = this.getPollingConfig();

    const interval$ = interval(intervalMs).pipe(map(() => 'interval'));
    const timeout$ = timer(timeoutMs).pipe(map(() => 'timeout'));

    merge(interval$, timeout$)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((source) => {
      if (source === 'timeout') {
        if (!this.stopped) {
          this.stopPolling();
        }
      } else if (source === 'interval') {
        this.fetchPaymentStatus();
        if (this.stopped) {
          this.stopPolling();
        }
      }
    });
  }

  private fetchPaymentStatus(): void {
    const { storeId, orderId, isStandalonePayment, redirectUrl, paymentTransactionId } = this.getPaymentDetails();

    const handleResponse = (status: string, provider?: string) => this.validatePaymentStatus(status, redirectUrl, provider);

    if (paymentTransactionId) {
      this.integrationPaymentService.fetchPaymentStatus(storeId, paymentTransactionId).subscribe(res => handleResponse(res.paymentStatus, res.provider));
    } else if (isStandalonePayment) {
      this.storeService.getStandaloneOrderPayment(storeId, orderId).subscribe(res => handleResponse(res.paymentStatus));
    } else {
      this.storeOrderService.get(storeId, orderId).subscribe(order => handleResponse(order.paymentStatus));
    }
  }

  private stopPolling(): void {
    this.stopped = true;
    this.cleanup();
    window.location.href = this.getRedirectUrl(this.provider);
    this.onStopFetchPaymentStatus();
  }

  private validatePaymentStatus(status: string, redirectUrl?: string, provider?: string): void {
    this.provider = provider;
    if (status !== "DRAFT" && status !== "IN_PROGRESS") {
      this.stopped = true;
      window.location.href = redirectUrl || this.getRedirectUrl(provider);
    }
  }

  protected abstract getPaymentDetails();

  protected abstract getPollingConfig(): {
    intervalMs: number;
    timeoutMs: number;
  };

  protected abstract getRedirectUrl(providerFromResponse: string): string;
  protected abstract onStopFetchPaymentStatus(): void;
}
